import keycloak from '@/auth/keycloak';
import { logDebugInfo } from '../utils/debug';

// Monkey patch WebSocket to track all instances and closures
const originalWebSocketClose = WebSocket.prototype.close;
WebSocket.prototype.close = function(code?: number, reason?: string) {
  console.log(`WebSocket close called with code: ${code}, reason: ${reason || 'none'}`);
  console.trace();
  return originalWebSocketClose.call(this, code, reason);
};

// Types for WebSocket messages
export interface WebSocketMessage {
  type: string;
  payload: any;
}

export interface RoomMessage {
  room_id: string;
  content: string;
  content_type: string;
  metadata?: Record<string, any>;
}

export interface DirectMessage {
  receiver_id: string;
  space_id: string;
  content: string;
  content_type: string;
  metadata?: Record<string, any>;
}

export interface TypingIndicator {
  room_id: string;
  is_typing: boolean;
}

export interface MessageReaction {
  room_id: string;
  message_id: string;
  emoji: string;
}

export interface UserPresence {
  status: 'online' | 'away' | 'busy' | 'offline';
}

// WebSocket connection states
export enum ConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
}

// WebSocket client for chat functionality
export class ChatSocketClient {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3; // Further reduced to be less aggressive
  private heartbeatInterval: number | null = null;
  private reconnectTimer: number | null = null;
  private minReconnectDelay = 3000; // Increased from 1s to 3s
  private initialReconnectDelay = 3000; // Increased from 1s to 3s
  private maxReconnectDelay = 60000; // Increased back to 60s for longer delays
  private state: ConnectionState = ConnectionState.DISCONNECTED;
  private messageHandlers: Map<string, (payload: any) => void> = new Map();
  private lastError: Error | null = null;
  private connectionPending = false; // Track pending connection attempts
  private manuallyDisconnected = false; // Track if disconnect was called intentionally
  private connectionTimestamp: number = 0; // Track when connection was established
  private subscribedRooms: Set<string> = new Set(); // Track subscribed rooms
  private circuitBreakerOpenUntil: number = 0; // Circuit breaker: stop trying until this timestamp
  private consecutiveFailures = 0; // Track consecutive connection failures
  private chatDisabled = false; // Flag to completely disable chat when backend is unavailable
  
  // Organization context for WebSocket connection
  private currentOrganizationId: string | null = null;
  private currentInstitutionId: string | null = null;

  // Get WebSocket URL from environment
  private getWebSocketUrl(): string {
    // Get the WebSocket URL from environment with no default fallback
    const envUrl = import.meta.env.VITE_CHAT_WS_URL;
    
    // Log the environment URL for debugging
    console.log('WebSocket environment URL:', envUrl);
    
    // Validate environment variable
    if (!envUrl) {
      console.warn('WARNING: Missing VITE_CHAT_WS_URL environment variable. Chat functionality will be disabled.');
      this.chatDisabled = true;
      throw new Error('Chat service unavailable: WebSocket URL not configured.');
    }
    
    // Make sure the URL includes the /wss path
    return envUrl.endsWith('/wss') ? envUrl : `${envUrl}/wss`;
  }
  
  // Set organization context for WebSocket connection
  setOrganizationContext(organizationId: string | null, institutionId: string | null): void {
    const previousOrgId = this.currentOrganizationId;
    const previousInstId = this.currentInstitutionId;
    
    // Only update if the values actually changed
    if (previousOrgId === organizationId && previousInstId === institutionId) {
      return; // No change, don't do anything
    }
    
    this.currentOrganizationId = organizationId;
    this.currentInstitutionId = institutionId;
    console.log('WebSocket: Organization context updated', { 
      from: { organizationId: previousOrgId, institutionId: previousInstId },
      to: { organizationId, institutionId }
    });
    
    // Only reconnect if we're connected AND we had previous context AND it's a meaningful change
    if (this.state === ConnectionState.CONNECTED && 
        previousOrgId !== null && previousInstId !== null && // Had previous context
        (previousOrgId !== organizationId || previousInstId !== institutionId)) {
      console.log('WebSocket: Organization context changed during active connection, reconnecting...');
      this.disconnect();
      setTimeout(() => this.connect(), 1000); // Reconnect after a short delay
    }
  }

  // Connect to WebSocket with token
  private async connectWithToken(token: string): Promise<boolean> {
    const wsUrl = this.getWebSocketUrl();
    let fullUrl = `${wsUrl}?token=${encodeURIComponent(token)}`;
    
    // Add organization_id parameter if available
    if (this.currentOrganizationId) {
      fullUrl += `&organization_id=${encodeURIComponent(this.currentOrganizationId)}`;
      console.log('WebSocket: Adding organization_id to connection:', this.currentOrganizationId);
    }
    
    console.log(`Connecting to WebSocket at: ${fullUrl}`);
    
    try {
      this.socket = new WebSocket(fullUrl);
      this.setupSocketListeners();
      
      // Wait for connection to complete or timeout
      const connected = await Promise.race([
        // Success: wait for open event
        new Promise<boolean>(resolve => {
          if (!this.socket) return resolve(false);
          
          const onOpen = () => {
            this.socket?.removeEventListener('open', onOpen);
            resolve(true);
          };
          
          this.socket.addEventListener('open', onOpen);
        }),
        
        // Timeout after 5 seconds
        new Promise<boolean>(resolve => setTimeout(() => resolve(false), 5000))
      ]);
      
      if (connected) {
        console.log('Successfully connected to WebSocket server');
        return true;
      }
      
      // Close socket if it's not already closed
      if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
        this.socket.close();
        this.socket = null;
      }
      
      console.error('WebSocket connection failed to open within timeout');
      return false;
    } catch (error) {
      console.error(`Failed to connect to WebSocket`, error);
      this.lastError = error instanceof Error ? error : new Error(String(error));
      
      // Clean up socket
      if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
        this.socket.close();
        this.socket = null;
      }
      
      return false;
    }
  }

  // Event callbacks
  private onStateChangeCallbacks: ((state: ConnectionState) => void)[] = [];
  private onErrorCallbacks: ((error: any) => void)[] = [];
  private onMessageCallbacks: ((message: WebSocketMessage) => void)[] = [];

  constructor() {
    // Register for Keycloak token updates
    keycloak.onTokenExpired = () => {
      this.refreshToken();
    };
  }

  // Connect to the WebSocket server
  async connect(): Promise<void> {
    // Don't connect if chat is disabled
    if (this.chatDisabled) {
      console.log('Chat Socket: Chat functionality is disabled, skipping connection');
      this.updateState(ConnectionState.ERROR);
      return;
    }

    // Check circuit breaker
    if (this.circuitBreakerOpenUntil > Date.now()) {
      const waitTime = Math.ceil((this.circuitBreakerOpenUntil - Date.now()) / 1000);
      console.log(`Chat Socket: Circuit breaker is open, waiting ${waitTime}s before next attempt`);
      return;
    }

    // Don't connect if already connecting, connected, or a connection is pending
    if (this.state === ConnectionState.CONNECTING || 
        this.state === ConnectionState.CONNECTED ||
        this.connectionPending) {
      return;
    }

    this.connectionPending = true;
    this.updateState(ConnectionState.CONNECTING);

    // Log debug information
    console.log('Attempting to connect to WebSocket...');
    logDebugInfo();

    // Ensure we have a valid token
    try {
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        console.log('Chat Socket: Token refreshed before connection');
      }
    } catch (error) {
      console.error('Chat Socket: Failed to refresh token before connection', error);
      this.updateState(ConnectionState.ERROR);
      this.handleError(error);
      return;
    }

    // Connect with token
    try {
      const token = keycloak.token;
      if (!token) {
        throw new Error('No authentication token available');
      }

      const connected = await this.connectWithToken(token);
      
      if (!connected) {
        throw this.lastError || new Error('Failed to connect to WebSocket');
      }

      // Reset failure counter on successful connection
      this.consecutiveFailures = 0;
      this.circuitBreakerOpenUntil = 0;
    } catch (error) {
      console.error('Chat Socket: Failed to connect', error);
      
      // Increment consecutive failures
      this.consecutiveFailures++;
      
      // Check if this looks like the backend is completely down
      const isBackendDown = error instanceof Error && (
        error.message.includes('Chat service unavailable') ||
        error.message.includes('WebSocket URL not configured') ||
        error.message.includes('failed to open within timeout') ||
        (this.lastError && this.lastError.message.includes('failed to open within timeout'))
      );

      if (isBackendDown || this.consecutiveFailures >= 3) {
        // Open circuit breaker for 5 minutes
        this.circuitBreakerOpenUntil = Date.now() + (5 * 60 * 1000);
        console.warn(`Chat Socket: Backend appears to be down or circuit breaker triggered. Disabling chat for 5 minutes.`);
        this.updateState(ConnectionState.ERROR);
        this.handleError(new Error('Chat service temporarily unavailable. Please try again later.'));
      } else {
        this.updateState(ConnectionState.ERROR);
        this.handleError(error);
        this.scheduleReconnect();
      }
    } finally {
      this.connectionPending = false; // Reset the flag regardless of success or failure
    }
  }

  // Set up socket event listeners
  private setupSocketListeners(): void {
    if (!this.socket) return;

    // Add reference to this for tracing purposes
    (this.socket as any).__chatSocketClient = this;

    // Connection opened
    this.socket.onopen = () => {
      console.log('Chat Socket: Connected successfully');
      this.connectionTimestamp = Date.now();
      this.updateState(ConnectionState.CONNECTED);
      this.connectionPending = false; // Ensure flag is reset when connection is established
      this.manuallyDisconnected = false; // Reset manual disconnect flag
      
      // Reset reconnect attempts after staying connected for a while
      setTimeout(() => {
        if (this.state === ConnectionState.CONNECTED) {
          console.log('Chat Socket: Connection stable, resetting reconnect counter');
          this.reconnectAttempts = 0;
        }
      }, 30000); // Reset after 30 seconds of stable connection
      
      this.startHeartbeat();
      
      // Resubscribe to rooms after reconnection
      this.resubscribeToRooms();
    };

    // Connection closed
    this.socket.onclose = (event) => {
      const connectionDuration = Date.now() - this.connectionTimestamp;
      console.log(`Chat Socket: Connection closed with code ${event.code}${event.reason ? `, reason: ${event.reason}` : ''}, connection duration: ${connectionDuration}ms`);
      
      // Print stack trace for unexpected closures
      if (!this.manuallyDisconnected && event.code !== 1000) {
        console.log('Chat Socket: Unexpected closure, current stack trace:');
        console.trace();
      }
      
      this.updateState(ConnectionState.DISCONNECTED);
      this.stopHeartbeat();

      // Handle specific close codes
      if (event.code === 4001) {
        // Token expired - refresh and reconnect
        console.log('Chat Socket: Token expired (code 4001), refreshing token and reconnecting');
        this.refreshToken().then(() => this.scheduleReconnect(1000));
      } else if (event.code === 1006) {
        // Abnormal closure - likely a network issue
        console.log('Chat Socket: Abnormal closure (code 1006), possibly a network or proxy issue');
        
        // Wait longer for network issues to avoid rapid reconnects
        const networkIssueDelay = 10000; // Increased from 5s to 10s
        this.scheduleReconnect(networkIssueDelay);
      } else if (event.code === 1005) {
        // No status code - could be from application code closing the socket
        console.log('Chat Socket: No status code closure (code 1005), possibly application forced closure');
        console.log('Current URL:', window.location.href);
        console.log('App state:', this.getDebugInfo());
        
        if (!this.manuallyDisconnected) {
          this.scheduleReconnect(2000);
        }
      } else if (event.code === 1000) {
        // Normal closure - don't reconnect automatically
        console.log('Chat Socket: Normal closure (code 1000), not reconnecting automatically');
      } else {
        // Other non-normal closure, attempt to reconnect
        console.log(`Chat Socket: Non-normal closure (code ${event.code}), attempting to reconnect`);
        this.scheduleReconnect();
      }
      
      // Reset manual disconnect flag
      this.manuallyDisconnected = false;
    };

    // Connection error
    this.socket.onerror = (error) => {
      console.error('Chat Socket: Error', error);
      this.updateState(ConnectionState.ERROR);
      this.handleError(error);
    };

    // Message received
    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        console.log('Chat Socket: Received message', message);
        
        // Special handling for auth_success
        if (message.type === 'auth_success') {
          console.log('Chat Socket: Authentication successful, connection established');
        }
        
        // Handle the message based on its type
        this.handleMessage(message);
        
        // Call all message callbacks
        this.onMessageCallbacks.forEach(callback => callback(message));
      } catch (error) {
        console.error('Chat Socket: Error parsing message', error);
        
        // If parsing fails, check if it's a binary message (native WebSocket ping/pong)
        if (typeof event.data !== 'string') {
          console.log('Chat Socket: Received native WebSocket frame (likely ping/pong)');
        }
      }
    };
  }

  // Refresh authentication token
  private async refreshToken(): Promise<boolean> {
    try {
      const refreshed = await keycloak.updateToken(30);
      return refreshed;
    } catch (error) {
      console.error('Chat Socket: Failed to refresh token', error);
      this.handleError(error);
      return false;
    }
  }

  // Schedule a reconnection attempt with exponential backoff
  private scheduleReconnect(fixedDelay?: number): void {
    // Don't schedule if chat is disabled or circuit breaker is open
    if (this.chatDisabled || this.circuitBreakerOpenUntil > Date.now()) {
      console.log('Chat Socket: Skipping reconnect - chat disabled or circuit breaker open');
      this.updateState(ConnectionState.ERROR);
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`Chat Socket: Max reconnection attempts (${this.maxReconnectAttempts}) reached, opening circuit breaker for 5 minutes`);
      this.circuitBreakerOpenUntil = Date.now() + (5 * 60 * 1000);
      this.updateState(ConnectionState.ERROR);
      return;
    }

    // Update state to reconnecting
    this.updateState(ConnectionState.RECONNECTING);

    // Clear any existing reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Increment reconnect attempts
    this.reconnectAttempts++;

    // Use provided fixed delay or calculate with exponential backoff and jitter
    let delay = fixedDelay || 0;
    
    if (!fixedDelay) {
      // Calculate base delay with exponential backoff
      const backoffDelay = this.initialReconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      const cappedDelay = Math.min(backoffDelay, this.maxReconnectDelay);
      
      // Apply full jitter: Random value between minReconnectDelay and calculated delay
      delay = Math.floor(this.minReconnectDelay + Math.random() * (cappedDelay - this.minReconnectDelay));
    }

    console.log(`Chat Socket: Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = window.setTimeout(() => {
      console.log(`Chat Socket: Attempting reconnection #${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
      this.connect();
    }, delay);
  }

  // Start heartbeat to keep connection alive
  private startHeartbeat(): void {
    // Clear any existing interval just to be safe
    this.stopHeartbeat();
    
    // The server handles ping/pong frames automatically:
    // T+54s: Server sends native WebSocket PING frame
    // T+54s: Browser automatically responds with native WebSocket PONG frame
    // No manual implementation needed on frontend
    console.log('Chat Socket: Relying on native WebSocket ping/pong mechanism (54s intervals)');
  }

  // Stop heartbeat
  private stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Send message to server
  send(message: WebSocketMessage): boolean {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('Chat Socket: Error sending message', error);
        this.handleError(error);
        
        // Try to reconnect if the send fails
        if (this.state === ConnectionState.CONNECTED) {
          console.log('Chat Socket: Send failed while connected, attempting to reconnect');
          this.disconnect();
          this.scheduleReconnect();
        }
        
        return false;
      }
    } else {
      if (this.state !== ConnectionState.CONNECTING && 
          this.state !== ConnectionState.RECONNECTING && 
          !this.connectionPending) {
        console.warn('Chat Socket: Cannot send message, socket not open. Attempting to reconnect.');
        this.connect();
      } else {
        console.warn('Chat Socket: Cannot send message, socket not open (already connecting)');
      }
      return false;
    }
  }

  // Handle incoming message
  private handleMessage(message: WebSocketMessage): void {
    // Call the specific handler for this message type if registered
    const handler = this.messageHandlers.get(message.type);
    if (handler) {
      handler(message.payload);
    }
  }

  // Update connection state and notify listeners
  private updateState(state: ConnectionState): void {
    this.state = state;
    this.onStateChangeCallbacks.forEach(callback => callback(state));
  }

  // Handle errors and notify listeners
  private handleError(error: any): void {
    this.onErrorCallbacks.forEach(callback => callback(error));
  }

  // Disconnect from the server
  disconnect(): void {
    console.log('Chat Socket: Manual disconnect called');
    console.trace(); // Log stack trace to see where disconnect is being called from
    
    this.manuallyDisconnected = true;
    this.stopHeartbeat();
    this.cancelReconnect();
    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }
    this.updateState(ConnectionState.DISCONNECTED);
  }

  // Get current connection state
  getState(): ConnectionState {
    return this.state;
  }

  // Register message type handler
  onMessageType(type: string, handler: (payload: any) => void): void {
    this.messageHandlers.set(type, handler);
  }

  // Register state change listener
  onStateChange(callback: (state: ConnectionState) => void): void {
    this.onStateChangeCallbacks.push(callback);
  }

  // Register error listener
  onError(callback: (error: any) => void): void {
    this.onErrorCallbacks.push(callback);
  }

  // Register message listener
  onMessage(callback: (message: WebSocketMessage) => void): void {
    this.onMessageCallbacks.push(callback);
  }

  // Remove state change listener
  removeStateChangeListener(callback: (state: ConnectionState) => void): void {
    this.onStateChangeCallbacks = this.onStateChangeCallbacks.filter(cb => cb !== callback);
  }

  // Remove error listener
  removeErrorListener(callback: (error: any) => void): void {
    this.onErrorCallbacks = this.onErrorCallbacks.filter(cb => cb !== callback);
  }

  // Remove message listener
  removeMessageListener(callback: (message: WebSocketMessage) => void): void {
    this.onMessageCallbacks = this.onMessageCallbacks.filter(cb => cb !== callback);
  }

  // Cancel any pending reconnection attempts
  private cancelReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // Subscribe to a room/group to receive messages
  subscribeToRoom(roomId: string): boolean {
    console.log(`Subscribing to group: ${roomId}`);
    this.subscribedRooms.add(roomId);
    return this.send({
      type: 'subscribe_group',
      payload: { group_id: roomId }
    });
  }

  // Unsubscribe from a room/group
  unsubscribeFromRoom(roomId: string): boolean {
    console.log(`Unsubscribing from group: ${roomId}`);
    this.subscribedRooms.delete(roomId);
    return this.send({
      type: 'unsubscribe_group',
      payload: { group_id: roomId }
    });
  }

  // Resubscribe to all rooms after reconnection
  resubscribeToRooms() {
    if (this.state !== ConnectionState.CONNECTED || !this.socket) {
      console.warn('Cannot resubscribe to rooms: Socket not connected');
      return;
    }

    console.log(`Resubscribing to ${this.subscribedRooms.size} rooms`);
    
    this.subscribedRooms.forEach(roomId => {
      this.send({
        type: 'subscribe_group',
        payload: { group_id: roomId }
      });
    });
  }

  // High level functions for common operations
  sendMessage(roomId: string, content: string, contentType = 'text/plain', metadata?: Record<string, any>): boolean {
    return this.send({
      type: 'room_message',
      payload: { 
        room_id: roomId, 
        content, 
        content_type: contentType,
        metadata
      }
    });
  }

  sendDirectMessage(
    toUserId: string,
    _institutionId: string,
    content: string,
    contentType = 'text/plain',
  ): boolean {
    console.log(`Sending direct message to user ${toUserId}`);
    return this.send({
      type: 'dm_message',
      payload: {
        receiver_id: toUserId,
        content,
        content_type: contentType,
      }
    });
  }

  sendTypingIndicator(
    targetId: string,
    isTyping: boolean,
    isDirect = false,
    /** Required for /chat (no /spaces/... in URL); falls back to getCurrentSpaceId() */
    directMessageSpaceId?: string | null,
  ): boolean {
    if (isDirect) {
      // For direct message typing indicator
      const spaceId = directMessageSpaceId || this.getCurrentSpaceId();
      console.log(`Sending DM typing indicator to user ${targetId}, isTyping: ${isTyping}, institution: ${spaceId}`);
      return this.send({
        type: 'dm_typing',
        payload: {
          receiver_id: targetId,
          institution_id: spaceId,
          is_typing: isTyping,
        }
      });
    } else {
      // For room typing indicator
      console.log(`Sending room typing indicator for room ${targetId}, isTyping: ${isTyping}`);
      return this.send({
        type: 'room_typing',
        payload: { 
          room_id: targetId, 
          is_typing: isTyping 
        }
      });
    }
  }

  // Helper to get current space ID from URL
  private getCurrentSpaceId(): string {
    // Try to extract space ID from URL path
    const match = window.location.pathname.match(/\/spaces\/([^\/]+)/);
    if (match && match[1]) {
      return match[1];
    }
    // Return default space ID if not found
    return 'c69235ce-f3d9-4881-a867-b5df13a17c42'; // Default space ID
  }

  sendReaction(roomId: string, messageId: string, emoji: string, isRemove: boolean = false): boolean {
    return this.send({
      type: 'room_reaction',
      payload: { 
        room_id: roomId,
        message_id: messageId, 
        emoji,
        is_remove: isRemove
      }
    });
  }

  sendDirectReaction(receiverId: string, messageId: string, emoji: string, isRemove: boolean = false): boolean {
    console.log(`Sending direct message reaction: ${emoji} for message ${messageId} to user ${receiverId}`);
    return this.send({
      type: 'dm_reaction',
      payload: {
        receiver_id: receiverId,
        message_id: messageId,
        emoji,
        is_remove: isRemove
      }
    });
  }

  sendThreadReaction(roomId: string, parentId: string, replyId: string, emoji: string, isRemove: boolean = false): boolean {
    console.log(`Sending thread reaction: ${emoji} for reply ${replyId} in thread ${parentId}, group ${roomId}, isRemove: ${isRemove}`);
    return this.send({
      type: 'thread_reaction',
      payload: { 
        group_id: roomId,
        parent_id: parentId,
        reply_id: replyId,
        emoji,
        is_remove: isRemove
      }
    });
  }

  updatePresence(status: 'online' | 'away' | 'busy' | 'offline'): boolean {
    return this.send({
      type: 'user_presence',
      payload: { status }
    });
  }

  markDmRead(receiverId: string, messageId: string): boolean {
    console.log(`Marking DM read: receiver=${receiverId}, message=${messageId}`);
    return this.send({
      type: 'mark_dm_read',
      payload: { receiver_id: receiverId, message_id: messageId }
    });
  }

  markRoomRead(roomId: string, messageId: string): boolean {
    console.log(`Marking room read: room=${roomId}, message=${messageId}`);
    return this.send({
      type: 'mark_room_read',
      payload: { room_id: roomId, message_id: messageId }
    });
  }

  // Reset connection state
  resetConnectionState(): void {
    if ((this.state === ConnectionState.ERROR || this.state === ConnectionState.DISCONNECTED) && !this.connectionPending) {
      // Reset reconnection attempts counter
      this.reconnectAttempts = 0;
      this.consecutiveFailures = 0;
      this.circuitBreakerOpenUntil = 0;
      this.cancelReconnect();
      
      console.log('Chat Socket: Resetting connection state and attempting to reconnect');
      this.connectionPending = true; // Set flag before scheduling
      window.setTimeout(() => {
        this.connect();
        // connect() will reset the connectionPending flag when done
      }, 1000);
    } else {
      console.log('Chat Socket: Connection reset skipped - already connecting or connection pending');
    }
  }

  // Force reset chat service (clears circuit breaker)
  forceReset(): void {
    console.log('Chat Socket: Force reset - clearing all failure states');
    this.chatDisabled = false;
    this.consecutiveFailures = 0;
    this.circuitBreakerOpenUntil = 0;
    this.reconnectAttempts = 0;
    this.cancelReconnect();
    this.resetConnectionState();
  }

  // Check if chat is currently disabled
  isChatDisabled(): boolean {
    return this.chatDisabled || this.circuitBreakerOpenUntil > Date.now();
  }

  // Get time until circuit breaker closes (in seconds)
  getCircuitBreakerWaitTime(): number {
    if (this.circuitBreakerOpenUntil <= Date.now()) return 0;
    return Math.ceil((this.circuitBreakerOpenUntil - Date.now()) / 1000);
  }

  // Get debug information
  private getDebugInfo(): any {
    return {
      state: this.state,
      reconnectAttempts: this.reconnectAttempts,
      connectionPending: this.connectionPending,
      manuallyDisconnected: this.manuallyDisconnected,
      socketReadyState: this.socket ? this.socket.readyState : 'no socket',
      lastError: this.lastError ? this.lastError.message : null,
      connectionDuration: this.connectionTimestamp ? (Date.now() - this.connectionTimestamp) : 0
    };
  }
}

// Create a singleton instance
export const chatSocketService = new ChatSocketClient();

export default chatSocketService; 